import axiosInstance, { setPodName } from '@helpers/Axios';

import { logError } from '@utils/axios/ErrorLogging';
import { getGuestUserData } from '@utils/GeneralUtils';
import CustomDomainVO from '@interfaces/CustomDomainVO';
import GuestProps from '@interfaces/guest/GuestProps';
import { ProfileGuestAccessVO } from '@interfaces/guest/ProfileGuestAccessVO';
import { logUserActivity } from '@service/ProfileService';
import { getUrlParam, removeUrlParam } from '@utils/URLParamUtil';
import { verifyProfileAccessRequest } from '@service/ProfileAccessManagementService';
import { deletePublicUserData, getPublicUserData, isPublicUserOtpCacheValid } from '@utils/profile/ProfileAccessManagementUtils';
import ProfileRequestAccessVO, { RequestedUserStatus } from '@interfaces/profiles/ProfileRequestAccessVO';
import { PROFILE_ERROR } from '@constants/ErrorConstants';
import { markOtpModalOpened } from '@utils/guest/GuestOtpModalGuard';

type GuestPageState = { loaded: boolean; isError: boolean; validated: boolean };

type PopulateGuestActions = {
  setGuestProps: (payload: Partial<GuestProps> | ProfileGuestAccessVO) => void;
  setRequestAccess: (payload: ProfileRequestAccessVO) => void;
  setOtpVerified: (payload: boolean) => void;
  setOpenValidateOtp?: (open: boolean) => void;
};

const errorLogger = (e: unknown, method: string) => {
  logError(e, 'UserLoadService.ts', method);
};

export const updatePageMetadata = (title: string, url: string) => {
  const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
  if (!link.getAttribute('rel')) {
    link.setAttribute('rel', 'icon');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
  document.title = (window.constants as { clientTitle?: string })?.clientTitle || title;
};

const hasLoop = (code: string) => {
  const loopPatterns = [/\bwhile\s*\(\s*true\s*\)/i, /\bfor\s*\(\s*;\s*;\s*\)/i, /\bdo\s*{[^}]*}\s*while\s*\(\s*true\s*\)/i, /\bfor\s*\(\s*\)/i, /\bwhile\s*\(\s*\)/i];
  return loopPatterns.some(pattern => pattern.test(code));
};

const hasMalformedScriptTag = (scriptContent: string) => {
  const tagPattern = /<\s*(\/?)\s*([a-z][a-z0-9]*)\s*>/gi;
  let match = tagPattern.exec(scriptContent);
  while (match) {
    const tagName = match[2].toLowerCase();
    if (tagName !== 'script' && /^sc/i.test(tagName) && /p/i.test(tagName)) {
      return true;
    }
    match = tagPattern.exec(scriptContent);
  }
  return false;
};

const detectMaliciousScript = (scriptContent: string) => {
  const redFlags = [/eval\(\s*\(/i, /window\.location/i, /javascript\s*:/i, /document\.(cookie|write)/i, /\.innerHTML\s*=/i];

  if (hasMalformedScriptTag(scriptContent)) {
    return true;
  }

  for (const pattern of redFlags) {
    if (pattern.test(scriptContent)) {
      return true;
    }
  }

  return false;
};

const detectSqlQuery = (scriptContent: string) => {
  const sqlPatterns = [
    /\bSELECT\b[\s\S]*?\bFROM\b/i,
    /\bINSERT\s+INTO\b/i,
    /\bUPDATE\b[\s\S]*?\bSET\b/i,
    /\bDELETE\s+FROM\b/i,
    /\bDROP\s+(TABLE|DATABASE)\b/i,
    /\bUNION\s+SELECT\b/i,
    /\bALTER\s+TABLE\b/i,
    /\bTRUNCATE\s+TABLE\b/i,
    /\bEXEC\s+\S/i,
    /\bEXECUTE\s+\S/i,
    /;\s*(?:SELECT|INSERT|UPDATE|DELETE|DROP)\b/i
  ];

  return sqlPatterns.some(pattern => pattern.test(scriptContent));
};

const isUnsafeScriptContent = (scriptContent?: string) => Boolean(scriptContent && (hasLoop(scriptContent) || detectMaliciousScript(scriptContent) || detectSqlQuery(scriptContent)));

export const injectUserScript = async (scriptId: string, scriptContent?: string) => {
  const trimmedContent = scriptContent?.trim();

  if (!trimmedContent || isUnsafeScriptContent(trimmedContent)) {
    return;
  }

  if (document.getElementById(scriptId)) {
    return;
  }

  if (/<script[\s>]/i.test(trimmedContent)) {
    const container = document.createElement('div');
    container.innerHTML = trimmedContent;

    const scripts = Array.from(container.querySelectorAll('script'));

    for (let i = 0; i < scripts.length; i++) {
      const sourceScript = scripts[i];
      const targetScript = document.createElement('script');

      if (i === 0) {
        targetScript.id = scriptId;
      }

      Array.from(sourceScript.attributes).forEach(attr => {
        targetScript.setAttribute(attr.name, attr.value);
      });

      if (sourceScript.textContent?.trim()) {
        targetScript.textContent = sourceScript.textContent;
      }

      if (sourceScript.src) {
        await new Promise<void>((resolve, reject) => {
          targetScript.onload = () => resolve();
          targetScript.onerror = () => reject(new Error(`Failed to load script: ${sourceScript.src}`));

          document.head.appendChild(targetScript);
        });
      } else {
        document.head.appendChild(targetScript);
      }
    }

    return;
  }

  const script = document.createElement('script');
  script.id = scriptId;
  script.type = 'text/javascript';
  script.async = true;
  script.textContent = trimmedContent;

  document.head.appendChild(script);
};

export const getDomainDetails = async (companyId: string, podName: string, onSuccess: (customDomainVO: CustomDomainVO, podName: string) => void, onFailed: () => void) => {
  try {
    const { data, status } = await axiosInstance().get(`/guest_v3/${companyId}/get-domain-details`, { params: { domain: window.location.hostname } });
    if (status === 200 && data.customDomainVO) {
      onSuccess(data.customDomainVO, podName);
    } else {
      onFailed();
    }
  } catch (e) {
    onFailed();
    errorLogger(e, 'getDomainDetails');
  }
};

export const getCompanyInfo = async (onSuccess: (companyId: string, podName: string) => void, onFailed: (status?: number) => void) => {
  try {
    const { data, status } = await axiosInstance().get(`/auth-custom-domain/verify`, { params: { domain: window.location.hostname } });
    if (status === 200) {
      onSuccess(data.companyId, data.podName);
    } else {
      onFailed(status);
    }
  } catch (e) {
    onFailed();
    errorLogger(e, 'getCompanyInfo');
  }
};

export const populateGuestProps = async (
  setPageState: (value: GuestPageState | ((prevState: GuestPageState) => GuestPageState)) => void,
  setErrorKey: (payload: string | null) => void,
  actions: PopulateGuestActions,
  params: {
    isPublicUser?: boolean;
    urlCompanyId?: string;
    urlProfileId?: string;
    urlGuestKey?: string;
    customDomainVO?: CustomDomainVO;
    domainPodName?: string;
  }
) => {
  const { setGuestProps, setRequestAccess, setOtpVerified, setOpenValidateOtp } = actions;
  const { isPublicUser, urlCompanyId, urlProfileId, urlGuestKey, customDomainVO, domainPodName } = params;

  const mailId = getUrlParam('mailId');
  const podName = domainPodName || getUrlParam('podName');
  const companyId = customDomainVO?.companyId || urlCompanyId;
  const profileId = customDomainVO?.entityId || urlProfileId;

  if (isPublicUser && (!companyId || !profileId)) {
    setPageState(prev => ({ ...prev, loaded: true, isError: true }));
    setErrorKey(PROFILE_ERROR.INVALID_LINK);
    return;
  }

  let guestKey = urlGuestKey;
  if (!isPublicUser && !guestKey) {
    const guestUserData = getGuestUserData();
    if (guestUserData.guestKey) {
      guestKey = guestUserData.guestKey;
    }
  }

  setGuestProps({
    loaded: true,
    isPublicUser,
    isCustomDomain: !!customDomainVO?.id,
    companyId,
    profileId,
    mailId: mailId || '',
    guestKey,
    isAccessVerified: !isPublicUser,
    isPublicUpdateEmail: false,
    podName: podName || '',
    isVerifiedUser: !isPublicUser
  });

  if (podName) {
    setPodName(podName);
  }

  if (isPublicUser && companyId && profileId && mailId) {
    const skipOtp = getUrlParam('skipOtp');
    verifyProfileAccessRequest(
      companyId,
      profileId,
      decodeURIComponent(mailId),
      false,
      false,
      false,
      undefined,
      (requestVO: ProfileRequestAccessVO) => {
        if (requestVO) {
          setRequestAccess(requestVO);
          const userData = getPublicUserData(requestVO.profileId, requestVO.mailId);
          if (requestVO.additionalParams?.skipOtpForPublicUpdate === true) {
            setGuestProps({ isPublicUpdateEmail: true });
            setOtpVerified(false);
          } else if (RequestedUserStatus.APPROVED === requestVO.status && isPublicUserOtpCacheValid(userData)) {
            setOtpVerified(true);
            if (requestVO?.additionalParams?.hasUserVerified) {
              logUserActivity(profileId, companyId, 'PROFILE_VIEWED');
            }
          } else {
            setOtpVerified(false);
            localStorage.removeItem('guestUserData');
            deletePublicUserData(requestVO.profileId, requestVO.mailId);
            if (RequestedUserStatus.APPROVED === requestVO.status) {
              markOtpModalOpened();
              setOpenValidateOtp?.(true);
            }
          }
          setPageState(prev => ({ ...prev, loaded: true, validated: true }));
        } else {
          setErrorKey(PROFILE_ERROR.INVALID_LINK);
          setPageState(prev => ({ ...prev, loaded: true, isError: true }));
        }
      },
      undefined,
      undefined,
      skipOtp || undefined
    );

    removeUrlParam(['mailId', 'fromMail']);
  } else {
    setPageState(prev => ({ ...prev, loaded: true, validated: Boolean(isPublicUser) }));
  }

  removeUrlParam(['error', 'emailTemplateId', 'skipOtp']);
};
